/**
 * Reporting API client.
 *
 * The reporting warehouse views do not exist yet, so every method currently
 * resolves against `mockReportingApi`. The HTTP path is written out alongside it
 * and typed against the same contract: flip `USE_MOCK_REPORTING_API` to false
 * (or set `VITE_REPORTING_USE_MOCK=false`) once the backend ships, and delete
 * `mockApi.ts` along with this switch.
 */

import apiClient from '../../utils/axiosConfig';
import { REPORTING_ENDPOINTS } from './constants';
import { mockReportingApi } from './mockApi';
import type { ReportingDataResponse, ReportingFilters, ReportingReferenceData } from './types';

const envFlag = (import.meta as { env?: { VITE_REPORTING_USE_MOCK?: string } }).env
  ?.VITE_REPORTING_USE_MOCK;

export const USE_MOCK_REPORTING_API = envFlag !== 'false';

/** Filters go on the query string as repeated params, which is what DRF expects. */
function toQueryParams(filters: ReportingFilters): Record<string, string | string[]> {
  return {
    written_from: filters.fromDate,
    written_to: filters.toDate,
    product: filters.products,
    carrier: filters.carrierIds,
    broker: filters.brokerIds,
    status: filters.statuses,
  };
}

export const reportingService = {
  async fetchPolicyRecords(filters: ReportingFilters): Promise<ReportingDataResponse> {
    if (USE_MOCK_REPORTING_API) return mockReportingApi.fetchPolicyRecords(filters);
    const response = await apiClient.get<ReportingDataResponse>(
      REPORTING_ENDPOINTS.POLICY_RECORDS,
      {
        params: toQueryParams(filters),
      }
    );
    return response.data;
  },

  async fetchReferenceData(): Promise<ReportingReferenceData> {
    if (USE_MOCK_REPORTING_API) return mockReportingApi.fetchReferenceData();
    const response = await apiClient.get<ReportingReferenceData>(REPORTING_ENDPOINTS.REFERENCE);
    return response.data;
  },
};
